import Axios, { AxiosResponse } from 'axios';
import { useAtom, useSetAtom } from 'jotai';
import { useRef } from 'react';
import { environment } from '../../environment';
import {
    WallSaveState,
    SharedProjectView,
    ShareLinkError,
    confirmingAtom,
    lastSavedSelectionsAtom,
    localSelectionsAtom,
    wallSaveStateAtom,
    shareErrorAtom,
    shareLoadingAtom,
    shareNoticeAtom,
    sharedProjectAtom
} from './types';

const DESIGNER_UPDATED_NOTICE = 'Your designer updated the options — please review your picks.';

type FetchProject = (token: string) => Promise<void>;
type SaveWallSelections = (token: string, wallId: number, designIds: number[]) => Promise<void>;
type ConfirmSelections = (token: string) => Promise<boolean>;
type RefreshMockups = (token: string) => Promise<void>;

export type InteriorShareFunctions = {
    fetchProject: FetchProject;
    saveWallSelections: SaveWallSelections;
    confirmSelections: ConfirmSelections;
    refreshMockups: RefreshMockups;
};

type UseInteriorShare = [
    SharedProjectView | null,
    InteriorShareFunctions
];

// #region mapStatusToError
const mapStatusToError = (status: number | undefined): ShareLinkError => {
    if (status === 401) return 'INVALID';
    if (status === 410) return 'EXPIRED';
    return 'GENERIC';
};

// #region sameIdSet
export const sameIdSet = (a: number[], b: number[]): boolean => {
    if (a.length !== b.length) return false;
    const set = new Set(a);
    return b.every((id: number) => set.has(id));
};

// #region useInteriorShare
// Public magic-link API — NO auth header on any call; the token in the URL is the credential.
export const useInteriorShare = (): UseInteriorShare => {
    const [project, setProject] = useAtom(sharedProjectAtom);
    const setError = useSetAtom(shareErrorAtom);
    const setLoading = useSetAtom(shareLoadingAtom);
    const setLocalSelections = useSetAtom(localSelectionsAtom);
    const setLastSaved = useSetAtom(lastSavedSelectionsAtom);
    const setSaveStates = useSetAtom(wallSaveStateAtom);
    const setConfirming = useSetAtom(confirmingAtom);
    const setNotice = useSetAtom(shareNoticeAtom);

    // Per-wall save sequence guards — only the latest PUT's outcome wins.
    const saveSeqRef = useRef<Record<number, number>>({});

    // #region seedFromProject
    const seedFromProject = (fresh: SharedProjectView) => {
        const selections: Record<number, number[]> = {};
        const saved: Record<number, number[]> = {};
        const states: Record<number, WallSaveState> = {};
        fresh.walls.forEach((wall) => {
            selections[wall.id] = [...wall.selected_design_ids];
            saved[wall.id] = [...wall.selected_design_ids];
            states[wall.id] = wall.selected_design_ids.length > 0 ? 'saved' : 'idle';
        });
        setLocalSelections(selections);
        setLastSaved(saved);
        setSaveStates(states);
    };

    // #region fetchProject
    const fetchProject: FetchProject = async (token) => {
        setLoading(true);
        setError(null);
        try {
            const response: AxiosResponse<SharedProjectView> = await Axios.get(
                `${environment.interiorShareUrl}/${token}`
            );
            setProject(response.data);
            seedFromProject(response.data);
        } catch (err: any) {
            setError(mapStatusToError(err?.response?.status));
        }
        setLoading(false);
    };

    // #region refetchAfterConflict
    // Silent refetch used when the backend tells us our view is stale (409/400).
    const refetchAfterConflict = async (token: string, notice: string | null) => {
        try {
            const response: AxiosResponse<SharedProjectView> = await Axios.get(
                `${environment.interiorShareUrl}/${token}`
            );
            setProject(response.data);
            seedFromProject(response.data);
            if (notice) setNotice(notice);
        } catch (err: any) {
            setError(mapStatusToError(err?.response?.status));
        }
    };

    // #region refreshMockups
    // Silent refresh of the project (picks up newly-generated mockup statuses/
    // URLs) WITHOUT flipping the page spinner or reseeding local picks — safe to
    // poll while the preview modal is open. Best-effort: errors are ignored.
    const refreshMockups: RefreshMockups = async (token) => {
        try {
            const response: AxiosResponse<SharedProjectView> = await Axios.get(
                `${environment.interiorShareUrl}/${token}`
            );
            setProject(response.data);
        } catch {
            // best-effort; a failed poll just retries on the next tick
        }
    };

    // #region setWallSaveState
    const setWallSaveState = (wallId: number, state: WallSaveState) => {
        setSaveStates((previous: Record<number, WallSaveState>) => ({ ...previous, [wallId]: state }));
    };

    // #region saveWallSelections
    const saveWallSelections: SaveWallSelections = async (token, wallId, designIds) => {
        const seq = (saveSeqRef.current[wallId] ?? 0) + 1;
        saveSeqRef.current[wallId] = seq;

        const uniqueIds = Array.from(new Set(designIds));
        setWallSaveState(wallId, 'saving');

        try {
            await Axios.put(
                `${environment.interiorShareUrl}/${token}/walls/${wallId}/selections`,
                { design_ids: uniqueIds }
            );
            if (saveSeqRef.current[wallId] !== seq) return; // stale — a newer save is in flight
            setWallSaveState(wallId, 'saved');
            setLastSaved((previous: Record<number, number[]>) => ({ ...previous, [wallId]: uniqueIds }));
        } catch (err: any) {
            if (saveSeqRef.current[wallId] !== seq) return;
            const status = err?.response?.status;
            if (status === 401 || status === 410) {
                setError(mapStatusToError(status));
            } else if (status === 409) {
                // Locked in another tab — refetch and switch to locked view.
                await refetchAfterConflict(token, null);
            } else if (status === 400) {
                // Designer edited recommendations — our option list is stale.
                await refetchAfterConflict(token, DESIGNER_UPDATED_NOTICE);
            } else {
                setWallSaveState(wallId, 'error');
            }
        }
    };

    // #region confirmSelections
    const confirmSelections: ConfirmSelections = async (token) => {
        setConfirming(true);
        try {
            await Axios.post(`${environment.interiorShareUrl}/${token}/confirm`);
            // Re-fetch instead of flipping status locally: per-wall auto-saves
            // update the backend but NOT the cached `project`, so the locked
            // "thank-you" view must read server truth — the new
            // SELECTIONS_COMPLETE status AND every wall's saved
            // selected_design_ids (+ mockup statuses).
            await fetchProject(token);
            setConfirming(false);
            return true;
        } catch (err: any) {
            const status = err?.response?.status;
            if (status === 401 || status === 410) {
                setError(mapStatusToError(status));
            } else if (status === 409) {
                // Already confirmed elsewhere — refetch shows the locked view.
                await refetchAfterConflict(token, null);
            } else if (status === 400) {
                // Walls incomplete server-side (designer edits cascaded) — resync.
                await refetchAfterConflict(token, DESIGNER_UPDATED_NOTICE);
            } else {
                setNotice('Something went wrong while confirming. Please try again.');
            }
            setConfirming(false);
            return false;
        }
    };

    return [project, { fetchProject, saveWallSelections, confirmSelections, refreshMockups }];
};
